import {
  getAllowedSsoHandoffUrls,
  SSO_AUTHORIZATION_HOST,
  SSO_AUTH_QUERY_KEYS,
  SSO_CALLBACK_PATH,
  SSO_PARTNERS,
  SSO_PROTOCOLS,
  SSO_PURCHASE_RETURN_PATHS,
} from "./ssoConfig";

export class SsoConfigurationError extends Error {
  constructor(message, code = "SSO_CONFIGURATION_ERROR") {
    super(message);
    this.name = "SsoConfigurationError";
    this.code = code;
  }
}

export function getSsoFlowFlags(protocol) {
  const normalizedProtocol = String(protocol || "").trim().toUpperCase();

  if (normalizedProtocol === SSO_PROTOCOLS.OIDC) {
    return { isLoginViaNu: true, isLoginViaEmu: false };
  }
  if (normalizedProtocol === SSO_PROTOCOLS.CAS) {
    return { isLoginViaNu: false, isLoginViaEmu: true };
  }

  throw new SsoConfigurationError(
    "The SSO provider returned an unsupported authentication protocol.",
    "UNSUPPORTED_SSO_PROTOCOL"
  );
}

function getSafeUnknownSsoDescriptor(descriptor) {
  const persistedDescriptor =
    descriptor && typeof descriptor === "object" && !Array.isArray(descriptor)
      ? descriptor
      : {};
  const protocol = String(persistedDescriptor.protocol || "UNKNOWN")
    .trim()
    .toUpperCase();

  return {
    ...persistedDescriptor,
    partner: null,
    protocol: protocol || "UNKNOWN",
    providerId: persistedDescriptor.providerId ?? null,
    providerName: persistedDescriptor.providerName || "Unknown persisted SSO",
  };
}

export function normalizePersistedSsoUserState(state) {
  if (!state || typeof state !== "object" || Array.isArray(state)) {
    return state;
  }

  if (state.ssoAuth != null) {
    if (typeof state.ssoAuth !== "object" || Array.isArray(state.ssoAuth)) {
      return {
        ...state,
        ...getSsoFlowFlags(SSO_PROTOCOLS.CAS),
        ssoAuth: getSafeUnknownSsoDescriptor(state.ssoAuth),
      };
    }

    try {
      const protocol = String(state.ssoAuth.protocol || "")
        .trim()
        .toUpperCase();
      const flags = getSsoFlowFlags(protocol);
      const normalizedPartner = String(state.ssoAuth.partner || "")
        .trim()
        .toUpperCase();

      return {
        ...state,
        ...flags,
        ssoAuth: {
          ...state.ssoAuth,
          partner: Object.values(SSO_PARTNERS).includes(normalizedPartner)
            ? normalizedPartner
            : null,
          protocol,
        },
      };
    } catch (error) {
      return {
        ...state,
        ...getSsoFlowFlags(SSO_PROTOCOLS.CAS),
        ssoAuth: getSafeUnknownSsoDescriptor(state.ssoAuth),
      };
    }
  }

  const legacyNuFlag = isEnabled(state.isLoginViaNu);
  const legacyEmuFlag = isEnabled(state.isLoginViaEmu);
  if (!legacyNuFlag && !legacyEmuFlag) return state;

  let partner = null;
  if (legacyNuFlag && !legacyEmuFlag) partner = SSO_PARTNERS.NU;
  if (legacyEmuFlag && !legacyNuFlag) partner = SSO_PARTNERS.EMU;

  return {
    ...state,
    ...getSsoFlowFlags(SSO_PROTOCOLS.CAS),
    ssoAuth: {
      partner,
      protocol: SSO_PROTOCOLS.CAS,
      providerId: null,
      providerName: "Legacy SSO",
    },
  };
}

function parseJson(value, fallback = null) {
  if (typeof value !== "string") return value;
  if (!value.trim()) return fallback;

  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
}

export function normalizeGrsPayload(response, fallback = null) {
  if (response == null) return fallback;

  let payload = response;
  if (Object.prototype.hasOwnProperty.call(response, "data")) {
    payload = response.data;
  }
  if (
    payload &&
    typeof payload === "object" &&
    Object.prototype.hasOwnProperty.call(payload, "d")
  ) {
    payload = payload.d;
  }

  return parseJson(payload, fallback) ?? fallback;
}

export function getLocationFromResponse(response) {
  const payload = normalizeGrsPayload(response, null);
  if (Array.isArray(payload)) return payload[0] || null;
  if (Array.isArray(payload?.locations)) return payload.locations[0] || null;
  if (Array.isArray(payload?.Locations)) return payload.Locations[0] || null;
  return payload && typeof payload === "object" ? payload : null;
}

function getSsoMetadata(location) {
  if (!location || typeof location !== "object") return null;
  return location.sso || location.SSO || location.Sso || null;
}

function isEnabled(value) {
  return value === true || String(value).toLowerCase() === "true";
}

function normalizeProvider(provider) {
  if (!provider || typeof provider !== "object") return null;
  const id = provider.id ?? provider.Id ?? provider.providerId;
  const name = provider.name ?? provider.Name ?? "";
  const authProtocol = String(
    provider.authProtocol ?? provider.AuthProtocol ?? ""
  ).toUpperCase();

  if (id == null || !authProtocol) return null;

  return {
    id,
    name: String(name),
    authProtocol,
    requiresRedirect: isEnabled(
      provider.requiresRedirect ?? provider.RequiresRedirect
    ),
    useLoginInfo: isEnabled(provider.useLoginInfo ?? provider.UseLoginInfo),
  };
}

export function readLocationSsoMetadata(locationOrResponse) {
  const directMetadata = getSsoMetadata(locationOrResponse);
  const location = directMetadata
    ? locationOrResponse
    : getLocationFromResponse(locationOrResponse);
  const metadata = getSsoMetadata(location);
  if (!metadata) {
    return { hasSso: false, providers: [] };
  }

  const providers = (metadata.providers || metadata.Providers || [])
    .map(normalizeProvider)
    .filter(Boolean);

  return {
    hasSso: isEnabled(metadata.hasSso ?? metadata.HasSso),
    providers,
  };
}

export function selectEligibleOidcProvider(locationOrResponse) {
  const metadata = readLocationSsoMetadata(locationOrResponse);
  if (!metadata.hasSso) return null;

  const providers = metadata.providers.filter(
    (provider) =>
      provider.authProtocol === SSO_PROTOCOLS.OIDC &&
      provider.requiresRedirect &&
      provider.useLoginInfo
  );

  if (providers.length > 1) {
    throw new SsoConfigurationError(
      "Multiple eligible OIDC providers were returned for this location.",
      "AMBIGUOUS_OIDC_PROVIDER"
    );
  }

  return providers[0] || null;
}

export function selectCasProvider(locationOrResponse) {
  const metadata = readLocationSsoMetadata(locationOrResponse);
  if (!metadata.hasSso) return null;

  return (
    metadata.providers.find(
      (provider) =>
        provider.authProtocol === SSO_PROTOCOLS.CAS && provider.useLoginInfo
    ) || null
  );
}

export function getSsoStartUrl(response) {
  const payload = normalizeGrsPayload(response, null);
  const location = payload?.location ?? payload?.Location;
  if (typeof location !== "string" || !location.trim()) {
    throw new SsoConfigurationError(
      "SSO/Start did not return an authorization URL.",
      "MISSING_AUTHORIZATION_URL"
    );
  }
  return location;
}

export function getSsoCallbackUrl(origin = window.location.origin) {
  return new URL(SSO_CALLBACK_PATH, origin).toString();
}

export function validateAuthorizationUrl(
  authorizationUrl,
  expectedCallbackUrl
) {
  let url;
  try {
    url = new URL(authorizationUrl);
  } catch (error) {
    throw new SsoConfigurationError(
      "The identity provider returned an invalid authorization URL.",
      "INVALID_AUTHORIZATION_URL"
    );
  }

  if (
    url.protocol !== "https:" ||
    url.host.toLowerCase() !== SSO_AUTHORIZATION_HOST ||
    url.username ||
    url.password
  ) {
    throw new SsoConfigurationError(
      "The identity provider returned an unsupported authorization host.",
      "UNTRUSTED_AUTHORIZATION_URL"
    );
  }

  const redirectUri = url.searchParams.get("redirect_uri");
  let normalizedRedirectUri = "";
  let normalizedExpectedCallback = "";
  try {
    normalizedRedirectUri = new URL(redirectUri || "").toString();
    normalizedExpectedCallback = new URL(expectedCallbackUrl).toString();
  } catch (error) {
    throw new SsoConfigurationError(
      "The identity provider returned an invalid callback URI.",
      "INVALID_CALLBACK_URI"
    );
  }

  if (normalizedRedirectUri !== normalizedExpectedCallback) {
    throw new SsoConfigurationError(
      "The identity provider callback URI did not match this application.",
      "CALLBACK_URI_MISMATCH"
    );
  }

  if (
    url.searchParams.get("response_type") !== "code" ||
    url.searchParams.get("response_mode") !== "query" ||
    !url.searchParams.get("state") ||
    !url.searchParams.get("nonce")
  ) {
    throw new SsoConfigurationError(
      "The identity provider returned an incomplete authorization request.",
      "INVALID_AUTHORIZATION_PARAMETERS"
    );
  }

  return url.toString();
}

export function sanitizePurchaseReturnPath(
  returnUrl,
  origin = window.location.origin
) {
  let url;
  try {
    url = new URL(returnUrl, origin);
  } catch (error) {
    throw new SsoConfigurationError(
      "The purchase continuation URL is invalid.",
      "INVALID_RETURN_PATH"
    );
  }

  if (
    url.origin !== new URL(origin).origin ||
    !SSO_PURCHASE_RETURN_PATHS.includes(url.pathname)
  ) {
    throw new SsoConfigurationError(
      "The purchase continuation URL is not allowed.",
      "UNTRUSTED_RETURN_PATH"
    );
  }

  for (const key of Array.from(url.searchParams.keys())) {
    if (SSO_AUTH_QUERY_KEYS.includes(key.toLowerCase())) {
      url.searchParams.delete(key);
    }
  }
  url.hash = "";

  return url.pathname + url.search;
}

export function validateLazIoHandoffUrl(
  candidate,
  hostname = window.location.hostname
) {
  if (!candidate) return null;

  let url;
  try {
    url = new URL(candidate);
  } catch (error) {
    throw new SsoConfigurationError(
      "The partner handoff URL is invalid.",
      "INVALID_HANDOFF_URL"
    );
  }

  if (
    url.username ||
    url.password ||
    url.search ||
    url.hash ||
    url.protocol !== "https:"
  ) {
    throw new SsoConfigurationError(
      "The partner handoff URL is not allowed.",
      "UNTRUSTED_HANDOFF_URL"
    );
  }

  const normalized = url.origin + url.pathname;
  const allowlist = getAllowedSsoHandoffUrls(hostname);

  if (!allowlist.includes(normalized)) {
    throw new SsoConfigurationError(
      "The partner handoff URL is not allowed.",
      "UNTRUSTED_HANDOFF_URL"
    );
  }

  return normalized;
}
